import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';

import { CheckboxStatus, SharedConstants, TableRowClicked } from '../../common';
import {
  RowActionWithData,
  TableBulkActionsWithSelectedElements,
  TableButtonSettings,
  TableColumn,
  TableConfigurations,
} from '../../interfaces';
import {
  CallBackFunctionPipe,
  CheckboxStatusCheckerPipe,
  FunctionBooleanPipe,
  GetNestedObjectValuesByString,
  IsAllSelectedPipe,
} from '../../pipes';
import { BaseComponent } from '../base-component/base.component';
import { ExpandableContentComponent } from '../expandable-content/expandable-content.component';
import { MatMenuModule } from '@angular/material/menu';
import { IconSvgComponent } from '../icon/svg-icon.component';
import { TableHeaderComponent } from '../table-header/table-header.component';
import { LocalStorageHelper } from '../../helper/local-storage-helper';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { debounceTime, fromEvent, map, startWith } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  imports: [
    // modules
    CommonModule,
    MatTableModule,
    MatCardModule,
    MatFormFieldModule,
    MatDividerModule,
    MatCheckboxModule,
    MatSortModule,
    TranslateModule,
    MatIconModule,
    MatMenuModule,

    // pipes
    IsAllSelectedPipe,
    FunctionBooleanPipe,
    CheckboxStatusCheckerPipe,
    GetNestedObjectValuesByString,
    CallBackFunctionPipe,

    // components
    ExpandableContentComponent,
    IconSvgComponent,
    TableHeaderComponent,
    EmptyStateComponent,
  ],
  providers: [IsAllSelectedPipe],
})
export class TableComponent<T, ButtonAction> extends BaseComponent implements OnInit, OnChanges {
  tableDataSource = new MatTableDataSource([]);
  selection: SelectionModel<T> = new SelectionModel<T>(true, []);
  excluded: SelectionModel<T> = new SelectionModel<T>(true, []);
  disableButtons: boolean;
  selectAll: boolean;
  displayedColumns: string[];
  checkBoxStatus: CheckboxStatus;
  buttonColumn: string;
  colorToggle: boolean;

  private tableAroundElements = ['.paginator', '.title-container', '.filters'];

  tableHeight$ = fromEvent(window, 'resize').pipe(
    startWith(window.innerHeight),
    debounceTime(250),
    map(() => {
      const spacing = 80;
      const totalHeight = window.innerHeight;
      const aroundElementsHeight = this.tableAroundElements.reduce((acc, elementSelector) => {
        const element = document.querySelector(elementSelector) as HTMLElement;
        return acc + (element?.offsetHeight || 0);
      }, 0);
      const total = totalHeight - aroundElementsHeight - spacing;
      return total ? `${total}px` : 'auto';
    })
  );

  // inputs
  @Input() title: string;
  @Input() titleIcon: string;
  @Input() tableConfigurations: TableConfigurations;
  @Input() set tableData(data: T[]) {
    this.setTableDataSource(data);
  }
  @Input() tableColumns: TableColumn[];
  @Input() customCellTemplate: TemplateRef<any>;
  @Input() buttons: TableButtonSettings<ButtonAction, T>[] = [];
  @Input() addToSelectionCallBackFunction?: (row: T, ...args) => boolean; // this option is to add a row to the selection if it passed the function

  // outputs
  @Output() rowActionData: EventEmitter<RowActionWithData<ButtonAction, T>> = new EventEmitter<
    RowActionWithData<ButtonAction, T>
  >();
  @Output() sortAction: EventEmitter<Sort> = new EventEmitter<Sort>();
  @Output() selectedItems: EventEmitter<TableBulkActionsWithSelectedElements<T>> = new EventEmitter<
    TableBulkActionsWithSelectedElements<T>
  >();

  //viewchild
  @ViewChild('checkboxRef') checkboxRef: MatCheckbox;

  //enums
  TableRowClicked = TableRowClicked;
  CheckboxStatus = CheckboxStatus;
  SharedConstants = SharedConstants;

  // constructor
  constructor(injector: Injector) {
    super(['Table'], injector);
    this.onActivateTableColors();
  }
  ngOnChanges(changes: SimpleChanges): void {
    // setup columns name
    if (changes && changes['tableColumns']) {
      this.setTableColumnsName();
    }
    // setup table configurations
    if (changes && changes['tableConfigurations']) {
      // setup table to be selectable
      if (this.tableConfigurations.isSelectable) {
        this.setTableSelectable();
      }
    }
    // setup table buttons column
    if (changes && changes['buttons']) {
      this.setupButtonsColumn();
    }
    if (changes && changes['tableData']) {
      this.setRowsChecked();
    }
  }
  ngOnInit(): void {}

  // publics

  onRowActionClicked(action: ButtonAction, element: T, index?: number): void {
    const rowActionWithData: RowActionWithData<ButtonAction, T> = {
      action: action,
      data: element,
      index: index,
    };
    this.rowActionData.emit(rowActionWithData);
  }

  hasVisibleButtons(element: T): boolean {
    if (!this.buttons || this.buttons.length === 0) {
      return false;
    }

    return this.buttons.some((button) => {
      // Check if button should be shown for this element
      if (button.showButtonsCallbackFunction) {
        return button.showButtonsCallbackFunction(element);
      }
      return true; // If no callback function, show the button
    });
  }
  masterToggle(): void {
    this.checkBoxStatus = null;
    if (this.selection.hasValue()) {
      this.selectAll = true;
      this.clearSelections();
      this.checkIfAllIsSelectedAndSetStatus();
    } else {
      this.selectAllRowsAndClearExclude();
      if (this.selection.hasValue()) {
        this.selectAll = true;
        this.disableButtons = true;
      } else {
        this.disableButtons = false;
        this.selectAll = false;
      }
    }
    this.onBulkActionsSelected('exclude');
  }

  checkedSelectRow(row: T) {
    if (this.selection.isSelected(row)) {
      this.excluded.select(row);
    }
    // toggle row if selected add it to selection and if it's deselected remove it
    this.selection.toggle(row);
    // check and set status type
    this.checkIfRowIsSelectedAndSetStatusType(row);
    if (this.selection.hasValue()) {
      this.disableButtons = true;
    } else {
      this.disableButtons = false;
    }
    this.onBulkActionsSelected(this.selectAll ? 'exclude' : 'include');
  }

  sortTable(sortParameters: Sort): void {
    const targetColumn: TableColumn = this.tableColumns.find((column) => column.name === sortParameters.active);
    sortParameters.active = targetColumn.dataKey;
    this.sortAction.emit(sortParameters);
  }

  onColorToggleChange(toggle: boolean): void {
    this.colorToggle = toggle;
    LocalStorageHelper.setItem(SharedConstants.LOCAL_STORAGE_TABLE_COLORS_KEY, this.colorToggle);
  }

  // privates
  private onBulkActionsSelected(type: 'exclude' | 'include') {
    let bulkActions: TableBulkActionsWithSelectedElements<T> = {
      items: this.excluded.selected,
      type: type,
      selected: this.selection.selected,
    };
    this.selectedItems.emit(bulkActions);
  }

  private setTableDataSource(data: T[]): void {
    this.tableDataSource = new MatTableDataSource<T>(data);
  }

  private setTableColumnsName(): void {
    this.displayedColumns = this.tableColumns.map((tableColumn: TableColumn) => tableColumn.name);
  }

  private setTableSelectable(): void {
    this.displayedColumns = ['select', ...this.displayedColumns];
  }

  private checkIfRowIsSelectedAndSetStatusType(row: T): void {
    if (this.selection.hasValue()) {
      if (this.selection.isSelected(row) && !this.isAllSelected()) {
        this.checkBoxStatus = CheckboxStatus.INDETERMINATE;
      } else if (this.selection.isSelected(row) && this.isAllSelected()) {
        this.checkBoxStatus = CheckboxStatus.ALL_SELECTED;
      } else {
        this.checkBoxStatus = CheckboxStatus.INDETERMINATE;
      }
    } else {
      this.checkBoxStatus = null;
    }
  }

  private clearSelections(): void {
    this.excluded.clear();
    this.selection.clear();
    this.disableButtons = false;
    this.checkBoxStatus = null;
    this.selectAll = false;
  }

  private checkIfAllIsSelectedAndSetStatus(): void {
    if (this.isAllSelected()) {
      this.checkBoxStatus = CheckboxStatus.ALL_SELECTED;
    } else {
      this.checkBoxStatus = CheckboxStatus.INDETERMINATE;
    }
  }

  private selectAllRowsAndClearExclude(): void {
    this.tableDataSource.data.forEach((row) => {
      if (this.addToSelectionCallBackFunction !== undefined) {
        if (this.addToSelectionCallBackFunction(row)) {
          this.selection.select(row);
        }
      } else {
        this.selection.select(row);
      }
    });
    this.excluded.clear();
  }

  private setupButtonsColumn(): void {
    if (this.buttons.length > 0) {
      this.buttonColumn = this.translationsList['Table']['Actions'];
      this.displayedColumns = [...this.displayedColumns, this.buttonColumn];
    }
  }

  private isAllSelected(): boolean {
    return this.tableDataSource.data.every((item) => this.selection.selected.includes(item));
  }

  private setRowsChecked(): void {
    if (this.selectAll) {
      this.tableDataSource.data.map((row) => {
        //this.selection.select(row);
        this.excluded.selected.forEach((item) => {
          if (row.id == item['id']) {
            this.selection.deselect(row);
            // this.excluded.deselect(item);
            //this.excluded.select(row);
          }
        });
      });
      this.checkBoxStatus = null;
      this.checkboxRef.checked = false;
    } else {
      this.selection.selected.forEach((item) => {
        this.tableDataSource.data.map((row) => {
          if (row.id == item['id']) {
            this.selection.deselect(row);
          }
        });
      });
      /*  this.excluded.selected.forEach((item) => {
        this.tableDataSource.data.map((row) => {
          if (row.id == item["id"]) {
            this.excluded.deselect(item);
            this.excluded.select(row);
          }
        });
      }); */
    }
    this.disableButtons = false;
    this.selection.clear();
  }

  private onActivateTableColors(): void {
    this.colorToggle =
      LocalStorageHelper.getItem(SharedConstants.LOCAL_STORAGE_TABLE_COLORS_KEY) === 'true' ? true : false;
  }
}
